/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import {
  Box,
  Flex,
  Menu,
  MenuButton,
  MenuDivider,
  MenuDividerProps,
  MenuItem,
  MenuList,
  Portal,
  Text,
} from "@chakra-ui/react";
import { JSX } from "react";

type ContextMenuItemProps = {
  title: string;
  shortcut: string | null;
  icon: JSX.Element | null;
  isDisabled?: boolean;
  onClick: () => void;
};

type ContextMenuDividerProps = MenuDividerProps;

export const ContextMenuItem = (option: ContextMenuItemProps) => {
  const { title, shortcut, icon, isDisabled, onClick } = option;
  return (
    <MenuItem isDisabled={isDisabled} onClick={onClick} as={Flex} gap={2}>
      {icon}
      <Text fontSize="sm" textColor="gray.700" flex={1}>
        {title}
      </Text>
      {shortcut && (
        <Text fontSize="sm" textColor="gray.400">
          {shortcut}
        </Text>
      )}
    </MenuItem>
  );
};

export const ContextMenuDivider = (props: ContextMenuDividerProps) => (
  <MenuDivider {...props} />
);

export type ContextMenuAnchor = { x: number; y: number };

type ContextMenuProps = {
  isOpen: boolean;
  anchor: ContextMenuAnchor | null;
  onClose: () => void;
  children: React.ReactNode;
};

export function ContextMenu({
  isOpen,
  anchor,
  onClose,
  children,
}: ContextMenuProps) {
  return (
    <Menu
      isOpen={isOpen}
      onClose={onClose}
      closeOnSelect
      strategy="fixed"
      placement="bottom-start"
    >
      {/* Single portal: anchor and popper share viewport coords (avoids drawer/transform). */}
      <Portal>
        <MenuButton
          as={Box}
          position="fixed"
          left={anchor ? `${anchor.x}px` : "0px"}
          top={anchor ? `${anchor.y}px` : "0px"}
          width="1px"
          height="1px"
          opacity={0}
          pointerEvents="none"
          zIndex={0}
          aria-hidden
        />
        <MenuList zIndex={1400}>{children}</MenuList>
      </Portal>
    </Menu>
  );
}
